import { createSlice } from '@reduxjs/toolkit';
import * as itemReducers from '../../../reducers/cartSlice.itemReducers';
import { selectMenuVersion } from '../../../reducers/menuSlice';
import { selectRestaurant } from '../../../reducers/restaurantSlice';
import logger from '../../../utils/logger';
import {
  resetSBOrderStates,
  resetSessionBoundaryData,
  setSessionStart,
} from '../sessionBoundary/sessionBoundary.slice';
import {
  freshDraftCart,
  IDraftCartState,
  initialDraftCartState,
} from './draftCart.props';

const SLICE_NAME = 'draftCart';

/**
 * Back to an empty draft. Called at every point a *new* HITL session begins —
 * never at session end and never on `car_exit`: a takeover lets the car leave
 * while the same session's order is still being finished, and clearing the
 * agent's state there is exactly what caused the dropped-order + cart-revert
 * incident. A draft they are still working from is that same kind of state.
 *
 * "A new session begins" is taken from the signals the app already has, rather
 * than re-derived from the message stream — the same four `agentMode` uses,
 * plus the two menu-identity resets below.
 */
const resetToFresh = (state: IDraftCartState): IDraftCartState => {
  const unpromoted = Object.keys(state.cartItems).length;
  if (unpromoted) {
    logger.info(`Draft cart cleared on session start (${unpromoted} item(s))`);
  }
  return freshDraftCart();
};

/**
 * HITL-1619 — a new line opens; the lines already there stay as the agent left
 * them.
 *
 * Both `addItemToCart` and `cloneCartItem` end in `expandCollapseCartItems`,
 * which folds every previously auto-expanded line shut so the newest is the only
 * one open. That is right for `cartSlice`, and in a live order it is also INERT
 * there: `shouldCollapse` (`utils/cart.ts:871`) additionally requires an order
 * `status`, so nothing in the AI's column is ever auto-collapsed in front of the
 * agent.
 *
 * A draft is never sent and so has no status — `DraftCartItemReview` says
 * exactly that — which left the cascade running unopposed in the one cart with
 * no counterweight to it. Copy a line in and its modifiers show; copy a second
 * and the first silently folds, which reads as the copy having dropped them.
 *
 * The two carts sit side by side showing the same items, so they collapse by the
 * same rule: only when the agent says so. Wrapped here rather than changed in
 * `cartSlice.itemReducers` — that function is shared, and `cartSlice`'s
 * post-send behaviour is not this ticket's to move.
 *
 * The snapshot holds only ids that were present BEFORE the write, so a line the
 * reducer has just created keeps whatever the reducer decided for it
 * (AUTO_EXPAND). The one case this would get wrong is a write that re-uses an id
 * already in the cart; no caller does that — every one of them mints from
 * `sequenceId`.
 */
const keepingExistingLinesOpen =
  <A>(apply: (state: IDraftCartState, action: A) => void) =>
  (state: IDraftCartState, action: A) => {
    const before = { ...state.cartItemCollapseStates };
    apply(state, action);
    Object.entries(before).forEach(([cartItemId, collapseState]) => {
      state.cartItemCollapseStates[Number(cartItemId)] = collapseState;
    });
  };

/**
 * HITL-1611 — a second cart store the AI's rebuild cannot reach.
 *
 * `useHypothesisFrames` dispatches `clearCart()` and rebuilds `cartSlice` from
 * scratch on every changed hypothesis frame, and `clearCart` returns
 * `initialCartState`. Anything the agent builds there while the AI is driving is
 * destroyed mid-order. This slice is where their work lives instead, which is
 * why the rest of the group cannot start anywhere else.
 *
 * The shared item reducers are the SAME functions `cartSlice` uses, imported from
 * `cartSlice.itemReducers` rather than copied, so the two carts cannot drift as
 * the cart evolves. RTK still mints separate action creators per slice
 * (`draftCart/addItemToCart` vs `cart/addItemToCart`), which is precisely the
 * distinction the agent-origin guard needs later.
 *
 * HITL-1611 was foundation only — nothing dispatched here and nothing rendered
 * it. HITL-1614 gave it a column and a copy control, HITL-1615 a way out into
 * the order, and HITL-1617 routes the centre pane's writes here, which is when
 * `applyOptionReplacement` had to join the shared set.
 */
export const draftCartSlice = createSlice({
  name: SLICE_NAME,
  initialState: initialDraftCartState,
  reducers: {
    addItemToCart: keepingExistingLinesOpen(itemReducers.addItemToCart),
    // HITL-1617: item conversion's second half. A combo-size pick on a My Cart
    // line re-picks the dependent side and drink sizes through this, and it has
    // to land in the draft — on `cartSlice` alone it would rewrite the AI's item.
    applyOptionReplacement: itemReducers.applyOptionReplacement,
    cloneCartItem: keepingExistingLinesOpen(itemReducers.cloneCartItem),
    declineWithheldGroups: itemReducers.declineWithheldGroups,
    deleteCartItem: itemReducers.deleteCartItem,
    selectModifier: itemReducers.selectModifier,
    selectModifiers: itemReducers.selectModifiers,
    setCartItemCollapseState: itemReducers.setCartItemCollapseState,
    setExplicitExpandState: itemReducers.setExplicitExpandState,
    updateQuantity: itemReducers.updateQuantity,

    /**
     * Session start, single-restaurant flow. sessionBoundary sets
     * `sessionStatus` to `start` inline on `car_enter` without dispatching
     * `setSessionStart`, so that path needs its own signal; useSessionStatus
     * dispatches this next to `resetAgentMode()` and the AI slice's own reset,
     * so the draft and the control state it belongs to can never disagree about
     * which session they are in.
     *
     * An earlier draft of this slice watched `messageReceived` for `car_enter`
     * itself and needed an `isCartEmpty` flag plumbed through the message
     * payload to avoid clearing while the previous order was still being
     * finished. That work is already done upstream: sessionBoundary only starts
     * a session when `!state.sessionId`, so an order still in flight keeps the
     * old session and none of these signals fire. `agentMode` removed the same
     * hand-rolled guard for the same reason.
     *
     * `cartSlice.clearCart` is a different action and never reaches here.
     */
    clearDraftCart: resetToFresh,

    /**
     * Empty the draft as one step of a larger operation the CALLER is logging —
     * `copyOrderIntoDraftCart` clearing before it copies, and
     * `replaceOrderWithDraftCart` clearing after it has written the order out.
     *
     * Separate from `clearDraftCart` only because of the log line. Both produce
     * the same state, but `clearDraftCart` says "on session start", and once
     * HITL-1649 made copy-all a clear-then-copy that sentence would have been
     * printed at info level on every press of a button that starts no session.
     * A log that names the wrong cause is worse than no log.
     */
    emptyDraftCart: () => freshDraftCart(),
  },
  extraReducers: (builder) => {
    // Session start, the task-router flow.
    builder.addCase(setSessionStart, resetToFresh);

    // Restaurant change and the task-router WS teardown; both between orders.
    builder.addCase(resetSessionBoundaryData, resetToFresh);

    // A draft holds CartItems built against one menu version. A restaurant or
    // version change leaves it holding items that no longer exist, so it clears
    // on both — `dialogSlice` resets on the same two for the same reason. This
    // is the one place the draft needs more than `agentMode`, which holds only
    // an enum.
    builder.addCase(selectRestaurant.fulfilled, resetToFresh);
    builder.addCase(selectMenuVersion.fulfilled, resetToFresh);

    /**
     * HITL-1619 — the order is over.
     *
     * `OrderActions.resetStatesAfterFinalOrderSubmit` dispatches this in the
     * SAME chain as `cartActions.clearCart()`, once per completed or cancelled
     * order. Without it the AI cart emptied and My Cart did not: the agent's
     * shadow of a finished order survived until the next car arrived, and in
     * the single-restaurant flow that window is unbounded.
     *
     * Keyed on this and deliberately NOT on `cartActions.clearCart()`, which is
     * the obvious hook and is a trap — `useHypothesisFrames` dispatches
     * `clearCart()` on every changed hypothesis frame, which is the exact event
     * this slice exists to survive. Listening to it would destroy the agent's
     * cart several times per conversation.
     *
     * Not `endSession` / `car_exit` either, for the reason given below: a
     * takeover lets the car leave while the same order is still being finished.
     * `resetSBOrderStates` is the narrower signal — not "the car went" but "the
     * order is done".
     */
    builder.addCase(resetSBOrderStates, (state) => {
      const unpromoted = Object.keys(state.cartItems).length;
      if (unpromoted) {
        logger.info(
          `Draft cart cleared on order completion (${unpromoted} unpromoted item(s))`
        );
      }
      return freshDraftCart();
    });
  },
});

export const draftCartActions = draftCartSlice.actions;
export const { clearDraftCart } = draftCartActions;

export default draftCartSlice.reducer;
